"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { Cookie, X, Settings, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import posthog from "posthog-js";

const COOKIE_CONSENT_KEY = "vetina-cookie-consent";

type ConsentType = "all" | "necessary" | null;

interface CookieConsentState {
  consent: ConsentType;
  timestamp: number;
}

export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    // Check if user has already given consent
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!stored) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }

    try {
      const { consent } = JSON.parse(stored) as CookieConsentState;
      if (consent === "all") {
        enableAllCookies();
      }
    } catch {
      setShowBanner(true);
    }
  }, []);

  const enableAllCookies = () => {
    if (typeof window === "undefined") return;
    try {
      posthog.opt_in_capturing();
    } catch {}
    if ((window as any).gtag) {
      (window as any).gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "granted",
      });
    }
  };

  const disableNonEssentialCookies = () => {
    if (typeof window === "undefined") return;
    try {
      posthog.opt_out_capturing();
    } catch {}
    if ((window as any).gtag) {
      (window as any).gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
      });
    }
  };

  const saveConsent = (consent: ConsentType) => {
    const state: CookieConsentState = {
      consent,
      timestamp: Date.now(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(state));

    if (consent === "all") {
      enableAllCookies();
    } else {
      disableNonEssentialCookies();
    }

    window.dispatchEvent(new CustomEvent("vetina:consent-changed", { detail: { consent } }));

    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    saveConsent("all");
  };

  const handleRejectAll = () => {
    saveConsent("necessary");
  };

  const handleSaveSettings = () => {
    if (analyticsEnabled || marketingEnabled) {
      saveConsent("all");
    } else {
      saveConsent("necessary");
    }
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <>
          {/* Backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9998]"
            aria-hidden="true"
          />

          {/* Cookie banner */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
          >
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              {!showSettings ? (
                // Main banner
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div className="hidden sm:flex flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 items-center justify-center">
                      <Cookie className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                        Ta strona wykorzystuje pliki cookies
                      </h2>
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        Używamy plików cookies, aby zapewnić najlepsze doświadczenie na naszej stronie.{" "}
                        <Link
                          href="/polityka-prywatnosci"
                          className="text-primary hover:underline font-medium"
                        >
                          Dowiedz się więcej
                        </Link>
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <Button
                          onClick={handleAcceptAll}
                          size="default"
                          className="w-full sm:w-auto"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Akceptuj wszystkie
                        </Button>
                        <Button
                          onClick={handleRejectAll}
                          variant="outline"
                          size="default"
                          className="w-full sm:w-auto"
                        >
                          Tylko niezbędne
                        </Button>
                        <Button
                          onClick={() => setShowSettings(true)}
                          variant="ghost"
                          size="default"
                          className="w-full sm:w-auto text-gray-600"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Ustawienia
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Settings panel
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Ustawienia cookies
                    </h2>
                    <button
                      onClick={() => setShowSettings(false)}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    {/* Necessary cookies - always enabled */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <h3 className="font-medium text-gray-900">Niezbędne</h3>
                        <p className="text-sm text-gray-600">
                          Wymagane do działania strony. Nie można ich wyłączyć.
                        </p>
                      </div>
                      <div className="w-12 h-6 bg-primary rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </div>
                    </div>

                    {/* Analytics cookies */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <h3 className="font-medium text-gray-900">Analityczne</h3>
                        <p className="text-sm text-gray-600">
                          Pomagają nam zrozumieć, jak korzystasz ze strony.
                        </p>
                      </div>
                      <button
                        onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                        className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                          analyticsEnabled ? "bg-primary justify-end" : "bg-gray-300 justify-start"
                        }`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </button>
                    </div>

                    {/* Marketing cookies */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <h3 className="font-medium text-gray-900">Marketingowe</h3>
                        <p className="text-sm text-gray-600">
                          Używane do personalizacji reklam.
                        </p>
                      </div>
                      <button
                        onClick={() => setMarketingEnabled(!marketingEnabled)}
                        className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                          marketingEnabled ? "bg-primary justify-end" : "bg-gray-300 justify-start"
                        }`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleSaveSettings}
                      size="lg"
                      className="flex-1"
                    >
                      Zapisz ustawienia
                    </Button>
                    <Button
                      onClick={handleAcceptAll}
                      variant="outline"
                      size="lg"
                      className="flex-1"
                    >
                      Akceptuj wszystkie
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// Hook to check cookie consent status
export function useCookieConsent(): ConsentType {
  const [consent, setConsent] = useState<ConsentType>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        const { consent } = JSON.parse(stored) as CookieConsentState;
        setConsent(consent);
      } catch {
        setConsent(null);
      }
    }
  }, []);

  return consent;
}
